<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizeObserver } from '@vueuse/core';
import { NODE_CONFIG, NODE_PICKER_CATEGORIES, ACTION_TYPES } from '../constants/nodeTypes.js';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['select', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);
const search = ref('');
const activeFilter = ref(0);

watch(
  () => props.show,
  val => {
    if (val) {
      search.value = '';
      activeFilter.value = 0;
      dialogRef.value?.open();
    } else {
      dialogRef.value?.close();
    }
  }
);

// Visible categories (exclude "end" — it only appears in "all")
const visibleCategories = computed(() =>
  NODE_PICKER_CATEGORIES.filter(c => c.key !== 'end')
);

const filterOptions = computed(() =>
  visibleCategories.value.map(c => ({ value: c.key, label: t(c.label) }))
);

// Map category key → category config for icon colors
const categoryMap = computed(() =>
  Object.fromEntries(NODE_PICKER_CATEGORIES.map(c => [c.key, c]))
);

const filteredItems = computed(() => {
  const filter = filterOptions.value[activeFilter.value].value;
  const q = search.value.toLowerCase();
  const items = [];

  // Action nodes — special: derived from ACTION_TYPES, not nodeConfig
  if (filter === 'all' || filter === 'action') {
    ACTION_TYPES.forEach(action => {
      const label = t(action.label);
      const description = t(action.description);
      if (!q || label.toLowerCase().includes(q) || description.toLowerCase().includes(q)) {
        items.push({ type: 'action', data: { action_name: action.key }, label, description, icon: action.icon, pickerCategory: 'action' });
      }
    });
  }

  // All other node types from nodeConfig
  Object.entries(NODE_CONFIG).forEach(([type, cfg]) => {
    if (!cfg.pickerCategory || type === 'action') return;
    if (cfg.pickerCategory === 'end' && filter !== 'all') return;
    if (filter !== 'all' && filter !== cfg.pickerCategory) return;
    const label = t(cfg.label);
    const description = t(cfg.description);
    if (q && !label.toLowerCase().includes(q) && !description.toLowerCase().includes(q)) return;
    items.push({ type, data: {}, label, description, icon: cfg.icon, pickerCategory: cfg.pickerCategory });
  });

  return items;
});

const tabRefs = ref([]);
const indicatorStyle = ref({});
const enableTransition = ref(false);

const activeElement = computed(() => tabRefs.value[activeFilter.value]);

const updateIndicator = () => {
  nextTick(() => {
    if (!activeElement.value) return;
    indicatorStyle.value = {
      left: `${activeElement.value.offsetLeft}px`,
      width: `${activeElement.value.offsetWidth}px`,
    };
  });
};

useResizeObserver(activeElement, updateIndicator);

watch([activeFilter, () => props.show], updateIndicator, { immediate: true });

onMounted(() => {
  nextTick(() => {
    enableTransition.value = true;
  });
});

const showDivider = index => {
  return (
    (index > activeFilter.value && index < filterOptions.value.length - 1) ||
    (index < activeFilter.value - 1 && index > -1)
  );
};

function onSelect(item) {
  emit('select', item.type, item.data);
}

function onClose() {
  emit('close');
}
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('FLOW_BUILDER.NODE_PICKER.TITLE')"
    width="2xl"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @close="onClose"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="search"
        type="search"
        :placeholder="t('FLOW_BUILDER.NODE_PICKER.SEARCH_PLACEHOLDER')"
        :custom-input-class="['ltr:!pl-8']"
        autofocus
      >
        <template #prefix>
          <span class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2" />
        </template>
      </Input>

      <div
        class="relative flex items-center h-8 rounded-lg bg-n-alpha-1 dark:bg-n-solid-1 w-full transition-all duration-200 ease-out has-[button:active]:scale-[1.01] [&>button]:flex-1"
      >
        <div
          class="absolute rounded-lg bg-n-solid-active shadow-sm pointer-events-none h-8 outline-1 outline outline-n-container inset-y-0"
          :class="{ 'transition-all duration-300 ease-out': enableTransition }"
          :style="indicatorStyle"
        />
        <template v-for="(opt, index) in filterOptions" :key="opt.value">
          <button
            :ref="el => (tabRefs[index] = el)"
            type="button"
            class="relative z-10 px-4 truncate py-1.5 text-sm border-0 outline-1 outline-transparent rounded-lg transition-all duration-200 ease-out hover:text-n-brand active:scale-[1.02]"
            :class="activeFilter === index ? 'text-n-blue-11 scale-100' : 'text-n-slate-10 scale-[0.98]'"
            @click="activeFilter = index"
          >
            {{ opt.label }}
          </button>
          <div
            v-if="index < filterOptions.length - 1"
            class="w-px h-3.5 rounded my-auto transition-colors duration-300 ease-in-out"
            :class="showDivider(index) ? 'bg-n-strong' : 'bg-transparent dark:bg-transparent'"
          />
        </template>
      </div>

      <div
        v-if="filteredItems.length === 0"
        class="flex items-center justify-center py-8 text-sm text-n-slate-9"
      >
        {{ t('FLOW_BUILDER.NODE_PICKER.EMPTY') }}
      </div>

      <div v-else class="grid grid-cols-2 gap-4 max-h-[400px] overflow-y-auto pr-1">
        <button
          v-for="(item, i) in filteredItems"
          :key="i"
          type="button"
          class="relative bg-n-solid-1 cursor-pointer rounded-2xl flex flex-col justify-start transition-all duration-200 ease-in py-5 px-4 items-start border border-solid border-n-weak hover:border-n-blue-9 hover:shadow-md text-left"
          @click="onSelect(item)"
        >
          <div class="flex items-start gap-3 w-full">
            <span
              class="flex items-center justify-center size-8 rounded-lg shrink-0 mt-0.5"
              :class="categoryMap[item.pickerCategory]?.iconBg || 'bg-n-alpha-2'"
            >
              <span
                :class="[item.icon, 'size-4', categoryMap[item.pickerCategory]?.iconText || 'text-n-slate-9']"
              />
            </span>
            <div class="flex flex-col items-start gap-1.5 min-w-0">
              <h3 class="text-n-slate-12 text-sm font-medium">{{ item.label }}</h3>
              <p v-if="item.description" class="text-n-slate-11 text-sm">{{ item.description }}</p>
            </div>
          </div>
        </button>
      </div>
    </div>
  </Dialog>
</template>
